/**
 * HangarWallCollisionBuilder
 * Creates wall collision meshes with BVH for precise collision detection
 * Supports door cutouts, positioned/rotated hangars
 */

import * as THREE from 'three'
import { OBB } from 'three/examples/jsm/math/OBB.js'
import { FEET_TO_METERS } from '@/constants/units'
import { ENABLE_COLLISION_DEBUG, DEBUG_WALL_COLLISIONS } from '@/constants/collision'
import type { HangarCollisionConfig, WallDefinition } from '@/types/collision'

// Debug logging state
let collisionDebugEnabled = ENABLE_COLLISION_DEBUG

/**
 * Enable wall collision debug logging
 */
export function enableWallDebug(): void {
  collisionDebugEnabled = true
}

/**
 * Disable wall collision debug logging
 */
export function disableWallDebug(): void {
  collisionDebugEnabled = false
}

/**
 * Debug logging utility for wall collisions
 */
function debugLog(message: string, data?: unknown): void {
  if (!collisionDebugEnabled || !DEBUG_WALL_COLLISIONS) return

  if (data) {
    console.log(`🧱 [WALL COLLISION] ${message}`, data)
  } else {
    console.log(`🧱 [WALL COLLISION] ${message}`)
  }
}

/**
 * Result of wall mesh creation
 */
export interface WallCollisionResult {
  /** OBBs for broad-phase collision detection */
  obbs: OBB[]
  /** Meshes with BVH for narrow-phase detection */
  meshes: THREE.Mesh[]
  /** Area ID this wall set belongs to */
  areaId: string
  /** Which wall each OBB/mesh represents */
  wallSides: Array<'back' | 'left' | 'right'>
}

/**
 * Create wall collision meshes with BVH for precise collision detection
 * Returns both OBBs (for broad phase) and meshes (for narrow phase)
 */
export function createHangarWallMeshes(config: HangarCollisionConfig): WallCollisionResult {
  const {
    areaId,
    width,
    depth,
    height,
    wallThickness,
    rotation = 0,
    position = [0, 0, 0],
    doors,
  } = config

  // Convert dimensions from feet to meters
  const widthMeters = width * FEET_TO_METERS
  const depthMeters = depth * FEET_TO_METERS
  const heightMeters = height * FEET_TO_METERS
  const thicknessMeters = wallThickness * FEET_TO_METERS

  // Convert hangar position from feet to meters
  const positionMeters = new THREE.Vector3(
    position[0] * FEET_TO_METERS,
    position[1] * FEET_TO_METERS,
    position[2] * FEET_TO_METERS
  )

  const halfWidth = widthMeters / 2
  const halfDepth = depthMeters / 2

  const obbs: OBB[] = []
  const meshes: THREE.Mesh[] = []
  const wallSides: Array<'back' | 'left' | 'right'> = []

  // Define wall geometries (no front wall - that's where the door is)
  const wallDefinitions: Array<WallDefinition & { side: 'back' | 'left' | 'right' }> = [
    // Back wall
    {
      position: [0, heightMeters / 2, -halfDepth],
      size: [widthMeters, heightMeters, thicknessMeters],
      side: 'back',
      doors: doors.back,
    },
    // Left wall
    {
      position: [-halfWidth, heightMeters / 2, 0],
      size: [thicknessMeters, heightMeters, depthMeters],
      side: 'left',
      doors: doors.left,
    },
    // Right wall
    {
      position: [halfWidth, heightMeters / 2, 0],
      size: [thicknessMeters, heightMeters, depthMeters],
      side: 'right',
      doors: doors.right,
    },
  ]

  // Build transformation matrix for rotation (if specified)
  const rotationRad = (rotation * Math.PI) / 180
  const hasRotation = rotation !== 0

  debugLog(
    `Creating walls for hangar ${areaId} at position [${position[0]}, ${position[1]}, ${position[2]}] feet (rotation: ${rotation}°)`
  )

  for (const wall of wallDefinitions) {
    // Create box geometry for wall at origin
    const geometry = new THREE.BoxGeometry(...wall.size)

    // Build transformation matrix: rotation + translation
    const transform = new THREE.Matrix4()

    // Calculate wall position
    const wallPosition = new THREE.Vector3(...wall.position)

    if (hasRotation) {
      // First rotate around origin
      transform.makeRotationY(rotationRad)

      // Apply rotation to the wall position (relative to hangar center)
      wallPosition.applyAxisAngle(new THREE.Vector3(0, 1, 0), rotationRad)

      // Add hangar position offset AFTER rotation
      wallPosition.add(positionMeters)
      transform.setPosition(wallPosition)
    } else {
      // No rotation - add hangar position to wall position
      wallPosition.add(positionMeters)
      transform.setPosition(wallPosition)
    }

    // PRE-TRANSFORM: Bake transformation directly into geometry vertices
    // This eliminates matrix transformation issues in BVH coordinate space
    geometry.applyMatrix4(transform)

    // Compute BVH on final transformed geometry
    geometry.computeBoundsTree()

    // Create mesh at origin with identity matrix (no transformation)
    // This ensures matrixWorld is always identity, simplifying BVH math
    const mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ visible: false }))
    mesh.name = `wall-${wall.side}-${areaId}`

    meshes.push(mesh)
    wallSides.push(wall.side)

    // Create OBB for broad phase from transformed geometry
    const positionAttribute = geometry.attributes.position as THREE.BufferAttribute
    const box = new THREE.Box3().setFromBufferAttribute(positionAttribute)
    const obb = new OBB().fromBox3(box)
    obbs.push(obb)

    debugLog(`Created ${wall.side} wall mesh with BVH`, {
      side: wall.side,
      size: wall.size,
      hasDoors: (wall.doors?.length ?? 0) > 0,
    })
  }

  return { obbs, meshes, areaId, wallSides }
}

/**
 * Create wall collision meshes for multiple hangars/areas
 */
export function createAllHangarWallMeshes(
  configs: HangarCollisionConfig[]
): Map<string, WallCollisionResult> {
  const results = new Map<string, WallCollisionResult>()

  for (const config of configs) {
    const result = createHangarWallMeshes(config)
    results.set(config.areaId, result)
  }

  debugLog(`Created wall meshes for ${configs.length} hangars`)

  return results
}

/**
 * Get aggregated OBBs and meshes from all hangars
 * Used for collision checking against all walls
 */
export function getAggregatedWallCollision(
  hangarWalls: Map<string, WallCollisionResult>
): {
  allOBBs: OBB[]
  allMeshes: THREE.Mesh[]
  wallToHangarMap: Map<number, { areaId: string; side: 'back' | 'left' | 'right' }>
} {
  const allOBBs: OBB[] = []
  const allMeshes: THREE.Mesh[] = []
  const wallToHangarMap = new Map<number, { areaId: string; side: 'back' | 'left' | 'right' }>()

  for (const [areaId, result] of hangarWalls) {
    const startIndex = allOBBs.length

    for (let i = 0; i < result.obbs.length; i++) {
      wallToHangarMap.set(startIndex + i, {
        areaId,
        side: result.wallSides[i],
      })
    }

    allOBBs.push(...result.obbs)
    allMeshes.push(...result.meshes)
  }

  return { allOBBs, allMeshes, wallToHangarMap }
}

/**
 * Legacy function for creating OBBs only (without BVH meshes)
 * Use createHangarWallMeshes for full BVH-enabled collision detection
 * @deprecated Use createHangarWallMeshes for better accuracy
 */
export function createHangarWallOBBs(config: {
  width: number
  depth: number
  height: number
  wallThickness: number
  rotation?: number
  position?: [number, number, number]
}): OBB[] {
  const { width, depth, height, wallThickness, rotation = 0, position = [0, 0, 0] } = config
  const widthMeters = width * FEET_TO_METERS
  const depthMeters = depth * FEET_TO_METERS
  const heightMeters = height * FEET_TO_METERS
  const thicknessMeters = wallThickness * FEET_TO_METERS

  // Convert position to meters
  const posX = position[0] * FEET_TO_METERS
  const posY = position[1] * FEET_TO_METERS
  const posZ = position[2] * FEET_TO_METERS

  const halfWidth = widthMeters / 2
  const halfDepth = depthMeters / 2
  const halfThickness = thicknessMeters / 2

  const walls: OBB[] = []

  // Back wall
  const backWallBox = new THREE.Box3(
    new THREE.Vector3(posX - halfWidth, posY, posZ - halfDepth - halfThickness),
    new THREE.Vector3(posX + halfWidth, posY + heightMeters, posZ - halfDepth + halfThickness)
  )
  walls.push(new OBB().fromBox3(backWallBox))

  // Left wall
  const leftWallBox = new THREE.Box3(
    new THREE.Vector3(posX - halfWidth - halfThickness, posY, posZ - halfDepth),
    new THREE.Vector3(posX - halfWidth + halfThickness, posY + heightMeters, posZ + halfDepth)
  )
  walls.push(new OBB().fromBox3(leftWallBox))

  // Right wall
  const rightWallBox = new THREE.Box3(
    new THREE.Vector3(posX + halfWidth - halfThickness, posY, posZ - halfDepth),
    new THREE.Vector3(posX + halfWidth + halfThickness, posY + heightMeters, posZ + halfDepth)
  )
  walls.push(new OBB().fromBox3(rightWallBox))

  // Apply rotation to all walls if rotation is specified
  if (rotation !== 0) {
    const rotationMatrix = new THREE.Matrix4().makeRotationY((rotation * Math.PI) / 180)
    walls.forEach((obb) => {
      obb.applyMatrix4(rotationMatrix)
    })
  }

  return walls
}
